import type { ChainOfferStepDto, OfferDto } from "../api/dto/offers.dto.js";

/** Lobby entry point and renderer of the "buy one, get the rest free" offer. */
export const ONE_PLUS_ONE_PLACEMENT = 'lobby_one_plus_one';
export const ONE_PLUS_ONE_TEMPLATE = 'one_plus_one';
export const ONE_PLUS_TWO_PLACEMENT = 'lobby_one_plus_two';
export const ONE_PLUS_TWO_TEMPLATE = 'one_plus_two';
export const ONE_PLUS_TEMPLATES: readonly string[] = [ ONE_PLUS_ONE_TEMPLATE, ONE_PLUS_TWO_TEMPLATE ];

export function onePlusTemplateForPlacement( placement_id: string ): string
{
  return placement_id === ONE_PLUS_TWO_PLACEMENT ? ONE_PLUS_TWO_TEMPLATE : ONE_PLUS_ONE_TEMPLATE;
}

/**
 * paid      — the purchase column, waiting for the store;
 * locked    — a free column the player has not reached yet;
 * claimable — the free column the chain cursor sits on;
 * claimed   — everything before the cursor.
 */
export type OnePlusOneStepStatus = 'paid' | 'locked' | 'claimable' | 'claimed';

export interface OnePlusOneStepState
{
  step: ChainOfferStepDto;
  index: number;
  status: OnePlusOneStepStatus;
}

export interface OnePlusOneState
{
  steps: OnePlusOneStepState[];
  /** The step the server cursor points at; null once the chain is complete. */
  cursor_id: string | null;
  all_claimed: boolean;
  /** True while the paid step is still the one to act on. */
  awaiting_purchase: boolean;
}

/** Analytics context shared by every event the 1+1 flow reports. */
export function onePlusOneAnalyticsContext( placement_id: string, chain_step_id?: string ): Record<string, unknown>
{
  return {
    placement: placement_id,
    placement_id,
    template: onePlusTemplateForPlacement( placement_id ),
    ...( chain_step_id ? { chain_step_id } : {} ),
  };
}

export function isOnePlusOneOffer( offer: OfferDto | null | undefined ): offer is OfferDto
{
  return Boolean( offer && offer.placement && ONE_PLUS_TEMPLATES.includes( offer.placement.template ) && offer.chain && offer.chain.steps.length > 0 );
}

/**
 * Derives per-column status from the server's single chain cursor: every
 * step before it is claimed, the cursor step is the one to buy or take, and
 * the rest wait behind a lock. A missing cursor key means the player has not
 * started (first step); an explicit null means the chain is finished.
 */
export function getOnePlusOneState( offer: OfferDto | null | undefined ): OnePlusOneState | null
{
  if ( !isOnePlusOneOffer( offer ) ) return null;
  const chain = offer.chain!;
  const steps = chain.steps;
  const has_cursor_key = 'next_step_id' in chain || 'next_paid_step_id' in chain;
  const raw_cursor = chain.next_step_id ?? chain.next_paid_step_id ?? null;
  const cursor_id = has_cursor_key ? raw_cursor : steps[0].id;
  const cursor_index = cursor_id === null ? steps.length : steps.findIndex( step => step.id === cursor_id );
  // An unknown cursor id can only come from a catalog edit mid-chain; show
  // everything as claimed rather than offering a step the server will refuse.
  const resolved_index = cursor_index < 0 ? steps.length : cursor_index;

  const states: OnePlusOneStepState[] = steps.map( ( step, index ) => ( {
    step,
    index,
    status: index < resolved_index
      ? 'claimed'
      : index > resolved_index
        ? 'locked'
        : step.free ? 'claimable' : 'paid',
  } ) );

  return {
    steps: states,
    cursor_id: resolved_index >= steps.length ? null : steps[resolved_index].id,
    all_claimed: resolved_index >= steps.length,
    awaiting_purchase: states.some( state => state.status === 'paid' ),
  };
}

/** Free steps the player has paid for but not taken yet; 0 until the purchase. */
export function getOnePlusOneUnclaimedCount( offer: OfferDto | null | undefined ): number
{
  const state = getOnePlusOneState( offer );
  if ( !state || state.awaiting_purchase ) return 0;
  return state.steps.filter( item => item.step.free && item.status !== 'claimed' ).length;
}

/** The free step the player can take right now, if any. */
export function getOnePlusOneClaimableStep( offer: OfferDto | null | undefined ): ChainOfferStepDto | null
{
  const state = getOnePlusOneState( offer );
  return state?.steps.find( item => item.status === 'claimable' )?.step ?? null;
}
